// SentItemPanel — the answer to "why the hell would you display a barren panel
// of information?" (§1).
//
// The order is Miles's (Q5), and it is state-dependent because the question
// changes with the state:
//
//   IN MOTION  where it stands + what I can do → who has it → what happened →
//              what it says → the document itself
//   FINISHED   what it says → the document, prominently → who signed it →
//              what happened (collapsed)
//
// Two rules the panel will not break. Empty blocks are never rendered — a
// draft has no recipients and no activity (T14), and a heading over nothing
// reads as data we failed to load (the WEL-144 §C2 rule, applied to the whole
// panel rather than to one group). And the panel describes THE VERSION BEING
// VIEWED (T10): the version strip re-fetches through the caller rather than
// re-labelling what is already on screen.
//
// Everything visible here is composed from components that already exist —
// ViewDocPanelView, ViewDocRecipients, ViewDocActivityRail, ViewDocPdfFrame,
// ViewDocReplicaView, ViewDocVersionStrip. None of them is forked (T30); where
// one cannot express something (the strip has no idea what `stillOpen` means)
// the value is composed into the props it already takes.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { toast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { OcEmpty, SignalChips, StatusChip } from '@/components/oc/bits';
import { ViewDocPanelView } from '@/components/viewDocs/ViewDocPanelView';
import { ViewDocRecipients } from '@/components/viewDocs/ViewDocRecipients';
import { ViewDocActivityRail } from '@/components/viewDocs/ViewDocActivityRail';
import { ViewDocPdfFrame } from '@/components/viewDocs/ViewDocPdfFrame';
import { ViewDocReplicaView } from '@/components/viewDocs/ViewDocReplicaView';
import { ViewDocVersionStrip } from '@/components/viewDocs/ViewDocVersionStrip';
import { fetchDocFile, type DocRecipient, type DocVersion } from '@/lib/viewDocsClient';
import {
  partialSendNotice,
  runItemAction,
  type ItemAction,
  type SentItemDetailResponse,
} from '@/lib/sentItemsClient';
import { SentItemIcon } from '@/components/sentItems/SentItemIcon';
import { leaseViewPath } from '@/lib/leaseViewPath';
import {
  BLOCK_HEADING,
  activityStartsCollapsed,
  glyphForKind,
  newestFirstCapped,
  nonEmptyBlocks,
  panelBlockOrder,
  relativeTime,
  restartActions,
  rowInMotion,
  rowStateTone,
  splitActions,
  whereLabel,
  type PanelBlock,
} from '@/components/sentItems/sentItemsView';

// ---------------------------------------------------------------------------
// Addressing
// ---------------------------------------------------------------------------

/**
 * The `(kind, id)` pair that addresses this item's CURRENT document through
 * the existing /api/docs loaders.
 *
 * `intake` is a ROW kind the registry does not know (T23), so an intake never
 * resolves to itself — it resolves to one of its sections, which are real
 * document kinds. When nothing resolves there is no document endpoint to talk
 * to, and the panel says less rather than guessing an address.
 */
function docTarget(detail: SentItemDetailResponse): { kind: string; id: string } | null {
  const current = detail.versions.find((v) => v.current) ?? detail.versions[0];
  if (current) return { kind: current.docKind, id: current.docId };
  const section = detail.sections?.[0];
  if (section) return { kind: section.docKind, id: section.docId };
  return null;
}

/** Versions in the shape the existing strip takes, with `stillOpen` said out
 *  loud in the one place the strip renders free text (T3).
 *
 *  Exported for W5: Full Details renders the strip in its own page header (the
 *  panel's header is suppressed there — a "Full Details" button on the Full
 *  Details page is a link to itself). The T3 label rule stays here, in one
 *  place, rather than being restated by the second caller. */
export function stripVersions(detail: SentItemDetailResponse): DocVersion[] {
  return detail.versions.map((v, i) => {
    const ordinal = detail.versions.length - i;
    const base = v.label?.trim() || `Version ${ordinal}`;
    const version: DocVersion = {
      id: v.id,
      label: v.stillOpen ? `${base} · still open` : base,
      state: v.state,
      stateLabel: v.stateLabel,
      createdAt: v.createdAt,
      current: v.current,
      viewable: v.viewable,
    };
    if (v.sentAt) version.sentAt = v.sentAt;
    return version;
  });
}

function actionError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `That didn't go through (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : "That didn't go through.";
}

// ---------------------------------------------------------------------------
// Download — the ONE place bytes leave the panel
// ---------------------------------------------------------------------------

/**
 * A download of the current version.
 *
 * Gated on the row's `viewable`, not on the presence of a URL: a W-9 carries
 * the owner's TIN and must never acquire a download button by a route that
 * only checks whether a file exists (T6). The bytes are fetched with the
 * session token like every other API call, then handed to the browser as a
 * blob — the /file route is authenticated, so a bare href would 401.
 */
function DownloadButton({
  target,
  filename,
  url,
}: {
  target: { kind: string; id: string };
  filename: string;
  url?: string | undefined;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="btn"
      data-variant="secondary"
      data-size="sm"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void (async () => {
          try {
            const blob = await fetchDocFile(target.kind, target.id, null, url);
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Long enough for the browser to have taken the bytes; the object
            // URL leaks for the life of the tab otherwise.
            setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
          } catch (err) {
            toast.error(actionError(err));
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      {busy ? 'Preparing…' : 'Download'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function ActionButtons({
  actions,
  onChanged,
}: {
  actions: ItemAction[];
  onChanged: () => void;
}): React.JSX.Element | null {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<ItemAction | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  if (actions.length === 0) return null;

  const { primary, more } = splitActions(actions);

  const run = async (action: ItemAction) => {
    setBusyId(action.id);
    try {
      const res = await runItemAction(action);
      // A Resend that reached some people and not others is a warning with the names of the
      // gap, not a "done" and not a failure to retry (a retry would email the ones reached).
      const partial = partialSendNotice(res);
      if (partial) toast.warning(partial, { title: action.label });
      else toast.success(`${action.label} — done.`);
      setConfirming(null);
      onChanged();
    } catch (err) {
      toast.error(actionError(err));
    } finally {
      setBusyId(null);
    }
  };

  // A destructive action states its consequence before it happens; an action
  // that carries no `confirm` and is not toned danger is a safe, repeatable
  // one and asking about it teaches people to click through dialogs.
  const start = (action: ItemAction) => {
    setMoreOpen(false);
    if (action.confirm || action.tone === 'danger') setConfirming(action);
    else void run(action);
  };

  const render = (action: ItemAction, inMenu: boolean) => {
    if (action.method === 'navigate') {
      return inMenu ? (
        <Link key={action.id} className="menu-item" to={action.endpoint}>
          {action.label}
        </Link>
      ) : (
        <Link
          key={action.id}
          className="btn"
          data-variant={action.tone === 'primary' ? undefined : 'secondary'}
          data-size="sm"
          to={action.endpoint}
        >
          {action.label}
        </Link>
      );
    }
    if (inMenu) {
      return (
        <button
          key={action.id}
          type="button"
          className="menu-item"
          data-tone={action.tone === 'danger' ? 'danger' : undefined}
          disabled={busyId !== null}
          onClick={() => start(action)}
        >
          {action.label}
        </button>
      );
    }
    return (
      <button
        key={action.id}
        type="button"
        className="btn"
        // T29: there is no `data-variant="primary"` rule — primary IS the
        // default button, so a primary action passes no variant at all.
        data-variant={
          action.tone === 'danger' ? 'danger' : action.tone === 'primary' ? undefined : 'secondary'
        }
        data-size="sm"
        disabled={busyId !== null}
        onClick={() => start(action)}
      >
        {busyId === action.id ? 'Working…' : action.label}
      </button>
    );
  };

  return (
    <div className="si-actions">
      {primary.map((a) => render(a, false))}
      {more.length > 0 && (
        <span className="si-more">
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            onClick={() => setMoreOpen((v) => !v)}
          >
            More
          </button>
          {moreOpen && (
            <>
              <span
                className="si-more-scrim"
                aria-hidden="true"
                onClick={() => setMoreOpen(false)}
              />
              <span className="menu si-more-menu" role="menu">
                {more.map((a) => render(a, true))}
              </span>
            </>
          )}
        </span>
      )}

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={`${confirming?.label ?? 'Continue'}?`}
        {...(confirming?.confirm ? { description: confirming.confirm } : {})}
        confirmLabel={confirming?.label ?? 'Continue'}
        tone={confirming?.tone === 'danger' ? 'danger' : 'primary'}
        busy={busyId !== null}
        onConfirm={() => {
          if (confirming) void run(confirming);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export interface SentItemPanelProps {
  detail: SentItemDetailResponse;
  /** Re-fetch: an action ran, or a recipient was sent to again. */
  onChanged: () => void;
  /** Version switching re-FETCHES through the caller — never re-labels (T10). */
  onSelectVersion?: (versionId: string) => void;
  /** Restrict which blocks render — the household file shows ①–③ inline. */
  blocks?: readonly PanelBlock[];
  /** How much activity the panel shows before deferring to Full Details. */
  activityCap?: number;
  /**
   * W5: Full Details renders the panel in TWO segments — the blocks before the
   * document, then the document (as the whole viewer), then the blocks after
   * it — so the §6.4 order survives with the real document in the document's
   * place. The second segment must not repeat the identity and the version
   * strip. Defaults to true; nothing else passes it.
   */
  showHeader?: boolean;
  /**
   * W5: Full Details expands ALL blocks (§6.5), including a finished item's
   * activity, which the drawer opens collapsed. Defaults to the state-dependent
   * rule (`activityStartsCollapsed`); pass `true` to open it.
   */
  activityStartsOpen?: boolean;
}

export function SentItemPanel({
  detail,
  onChanged,
  onSelectVersion,
  blocks,
  activityCap = 8,
  showHeader = true,
  activityStartsOpen,
}: SentItemPanelProps): React.JSX.Element {
  const { row } = detail;
  const inMotion = rowInMotion(row);
  const [activityOpen, setActivityOpen] = useState(
    activityStartsOpen ?? !activityStartsCollapsed(inMotion),
  );

  const target = docTarget(detail);
  const where = whereLabel(row);
  const recipients = detail.recipients ?? [];
  const activity = detail.activity ?? [];
  const sectionsWithReplica = (detail.sections ?? []).filter((s) => s.replica);
  const hasTerms = detail.panel.groups.some((g) => g.rows.length > 0);
  const hasDocument = Boolean(
    (row.viewable && target && detail.file?.available !== false) ||
      detail.replica ||
      sectionsWithReplica.length > 0,
  );

  const order = nonEmptyBlocks(blocks ?? panelBlockOrder(inMotion), {
    recipients: recipients.length > 0,
    activity: activity.length > 0,
    terms: hasTerms,
    document: hasDocument,
  });

  // A resend has to be addressed to a document; with no document endpoint the
  // affordance is withdrawn rather than offered and left to 404.
  const resendable: DocRecipient[] = target
    ? recipients
    : recipients.map((r) => ({ ...r, canResend: false }));

  const { entries: shownActivity, hidden: hiddenActivity } = newestFirstCapped(
    activity,
    activityCap,
  );

  const renderBlock = (block: PanelBlock): React.JSX.Element | null => {
    switch (block) {
      case 'standing':
        return (
          <section className="oc-panel si-block" data-block="standing" key="standing">
            <div className="oc-kicker">{BLOCK_HEADING.standing}</div>
            <div className="si-standing">
              <StatusChip tone={rowStateTone(row)}>{row.stateLabel}</StatusChip>
              <span className="si-standing-step">{row.lifecycle.label}</span>
            </div>
            {row.lifecycle.detail && (
              <div
                className={row.lifecycle.blocked ? 'si-standing-blocked' : 'oc-meta'}
                {...(row.lifecycle.blocked ? { role: 'status' } : {})}
              >
                {row.lifecycle.detail}
              </div>
            )}
            {row.waitingOn && (
              <div className="si-waiting">
                Waiting on <b>{row.waitingOn.label}</b>
              </div>
            )}
            {row.signals.block + row.signals.attention + row.signals.notice > 0 && (
              <div className="si-standing-signals">
                <SignalChips counts={row.signals} labeled />
              </div>
            )}
            <ActionButtons actions={detail.actions ?? []} onChanged={onChanged} />
          </section>
        );

      case 'recipients':
        return (
          <section className="oc-panel si-block" data-block="recipients" key="recipients">
            <div className="oc-kicker">{BLOCK_HEADING.recipients}</div>
            <ViewDocRecipients
              kind={target?.kind ?? row.kind}
              id={target?.id ?? row.itemId}
              recipients={resendable}
              onChanged={onChanged}
            />
            {detail.pending && detail.pending.length > 0 && (
              <>
                <div className="oc-kicker si-subkicker">Still to happen</div>
                <ul className="vd-pending">
                  {detail.pending.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </>
            )}
          </section>
        );

      case 'activity':
        return (
          <section className="oc-panel si-block" data-block="activity" key="activity">
            <div className="si-block-head">
              <span className="oc-kicker">{BLOCK_HEADING.activity}</span>
              {!inMotion && (
                <button
                  type="button"
                  className="btn"
                  data-variant="ghost"
                  data-size="sm"
                  aria-expanded={activityOpen}
                  onClick={() => setActivityOpen((v) => !v)}
                >
                  {activityOpen ? 'Hide' : `Show (${activity.length})`}
                </button>
              )}
            </div>
            {activityOpen && (
              <>
                {/* The API sends activity newest LAST (T18); the reversal
                    happens here, in the view, not on the wire. */}
                <ViewDocActivityRail activity={shownActivity} />
                {hiddenActivity > 0 && (
                  <div className="oc-meta si-more-activity">
                    {hiddenActivity} older {hiddenActivity === 1 ? 'entry' : 'entries'} — all
                    activity is in Full Details.
                  </div>
                )}
              </>
            )}
          </section>
        );

      case 'terms':
        return (
          <section className="oc-panel si-block" data-block="terms" key="terms">
            <div className="oc-kicker">{BLOCK_HEADING.terms}</div>
            <ViewDocPanelView panel={detail.panel} />
            {detail.sections && detail.sections.length > 0 && (
              <div className="si-sections">
                {/* A section nobody was asked is SHOWN, muted — absence is the
                    information (§14 Q1), and hiding it would read as complete. */}
                {detail.sections.map((section) => (
                  <span
                    key={section.section}
                    className="tag si-section"
                    data-status={section.status}
                  >
                    {section.label}
                    <span className="si-section-status">
                      {section.status === 'not_requested'
                        ? 'not requested'
                        : section.status === 'completed'
                          ? 'complete'
                          : section.status === 'cancelled'
                            ? 'cancelled'
                            : 'open'}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </section>
        );

      case 'document':
        return (
          <section className="oc-panel si-block" data-block="document" key="document">
            <div className="si-block-head">
              <span className="oc-kicker">{BLOCK_HEADING.document}</span>
              {row.viewable && target && detail.file?.available !== false && (
                <DownloadButton
                  target={target}
                  filename={detail.file?.filename ?? `${row.kind}.pdf`}
                  url={detail.file?.url}
                />
              )}
            </div>
            {detail.replica ? (
              <ViewDocReplicaView replica={detail.replica} />
            ) : sectionsWithReplica.length > 0 ? (
              <div className="si-section-replicas">
                {sectionsWithReplica.map((section) => (
                  <div key={section.section}>
                    <div className="oc-kicker si-subkicker">{section.label}</div>
                    <ViewDocReplicaView replica={section.replica!} />
                  </div>
                ))}
              </div>
            ) : target ? (
              <ViewDocPdfFrame
                kind={target.kind}
                id={target.id}
                versionId={null}
                title={`${row.kindLabel} — ${row.subject}`}
                {...(detail.file ? { availability: detail.file } : {})}
              />
            ) : (
              <OcEmpty>No document has been produced yet.</OcEmpty>
            )}
          </section>
        );
    }
  };

  return (
    <div className="si-panel" data-motion={inMotion ? 'in-motion' : 'finished'}>
      {showHeader && (
        <div className="si-panel-head" data-block="header">
          <div className="si-panel-ident">
            <SentItemIcon glyph={glyphForKind(row.kind)} />
            <span className="oc-kicker">{row.kindLabel}</span>
            {where && <span className="oc-meta">{where}</span>}
            {!inMotion && row.completedAt && (
              <span className="oc-meta" title={row.completedAt}>
                finished {relativeTime(row.completedAt)}
              </span>
            )}
          </div>
          <span style={{ display: 'inline-flex', gap: 'var(--space-2)' }}>
            {row.kind === 'lease' && row.groupId && (
              <Link
                className="btn"
                data-variant="secondary"
                data-size="sm"
                to={leaseViewPath(row.groupId)}
              >
                View lease
              </Link>
            )}
            <Link
              className="btn"
              data-size="sm"
              to={`/items/${encodeURIComponent(row.itemId)}`}
            >
              Full Details
            </Link>
          </span>
        </div>
      )}

      {showHeader && row.versionCount > 1 && detail.versions.length > 1 && (
        <ViewDocVersionStrip
          versions={stripVersions(detail)}
          selectedId={detail.selectedVersionId || null}
          onSelect={(versionId) => onSelectVersion?.(versionId)}
        />
      )}

      {/* A cancelled item is finished, so it has no standing block to hang "Ask again" on. */}
      {showHeader && !inMotion && restartActions(detail.actions ?? []).length > 0 && (
        <div className="oc-panel si-block" data-section="restart">
          <ActionButtons actions={restartActions(detail.actions ?? [])} onChanged={onChanged} />
        </div>
      )}

      {order.map(renderBlock)}
    </div>
  );
}
